const TELEMETRY_QUEUE_KEY = 'pulsefit_error_telemetry_queue';
const MAX_QUEUE_SIZE = 50;

const SENSITIVE_KEYS = new Set([
  'token',
  'access_token',
  'refresh_token',
  'password',
  'apikey',
  'api_key',
  'customkey',
  'email',
  'user_id',
  'userid',
  'heartrateseries',
  'notes',
  'workoutnotes',
  'authorization'
]);

/**
 * Scrubs PII (JWTs, emails, UUIDs, API keys, and sensitive HealthKit fields)
 * from any string, Error, Array, or Object before logging or transmitting telemetry.
 *
 * @param {*} input
 * @returns {*}
 */
export function scrubPII(input) {
  if (input == null) return input;

  if (typeof input === 'string') {
    return input
      .replace(/Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/gi, 'Bearer [REDACTED_TOKEN]')
      .replace(/\beyJ[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\b/g, '[REDACTED_JWT]')
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[REDACTED_EMAIL]')
      .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[REDACTED_UUID]')
      .replace(/\bAIza[0-9A-Za-z\-_]{30,}\b/g, '[REDACTED_API_KEY]');
  }

  if (Array.isArray(input)) {
    return input.map(item => scrubPII(item));
  }

  if (typeof input === 'object') {
    const scrubbed = {};
    for (const [key, val] of Object.entries(input)) {
      if (SENSITIVE_KEYS.has(String(key).toLowerCase())) {
        scrubbed[key] = '[REDACTED]';
      } else {
        scrubbed[key] = scrubPII(val);
      }
    }
    return scrubbed;
  }

  return input;
}

let memoryFallbackQueue = [];

/**
 * Returns queued PII-scrubbed telemetry error envelopes from localStorage or memory fallback.
 * @returns {Array<Object>}
 */
export function getQueuedTelemetryErrors() {
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function') {
      const raw = localStorage.getItem(TELEMETRY_QUEUE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (_) {
    // fallback to memory queue
  }
  return [...memoryFallbackQueue];
}

/**
 * Clears the local telemetry error queue.
 */
export function clearQueuedTelemetryErrors() {
  memoryFallbackQueue = [];
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.removeItem === 'function') {
      localStorage.removeItem(TELEMETRY_QUEUE_KEY);
    }
  } catch (_) {
    // ignore storage errors
  }
}

/**
 * Captures, scrubs PII, queues, and forwards an application or bridge error.
 *
 * @param {Error|string|Object} error
 * @param {Object} [context={}]
 * @returns {Object} The scrubbed telemetry event envelope
 */
export function reportError(error, context = {}) {
  const rawMessage = error instanceof Error
    ? error.message
    : (typeof error === 'string' ? error : JSON.stringify(error || 'Unknown error'));
  const rawName = error instanceof Error ? error.name : 'ApplicationError';
  const rawStack = error instanceof Error && error.stack ? String(error.stack) : '';

  const event = {
    timestamp: new Date().toISOString(),
    name: scrubPII(rawName),
    message: scrubPII(rawMessage),
    stack: scrubPII(rawStack).slice(0, 2000),
    context: scrubPII(context || {})
  };

  memoryFallbackQueue.push(event);
  while (memoryFallbackQueue.length > MAX_QUEUE_SIZE) {
    memoryFallbackQueue.shift();
  }

  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.setItem === 'function') {
      const queue = getQueuedTelemetryErrors();
      queue.push(event);
      while (queue.length > MAX_QUEUE_SIZE) {
        queue.shift();
      }
      localStorage.setItem(TELEMETRY_QUEUE_KEY, JSON.stringify(queue));
    }
  } catch (_) {
    // ignore storage quota errors
  }

  try {
    if (typeof window !== 'undefined') {
      if (window.Sentry && typeof window.Sentry.captureException === 'function') {
        window.Sentry.captureException(new Error(event.message), { extra: event.context });
      }
      if (
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.telemetryHandler &&
        typeof window.webkit.messageHandlers.telemetryHandler.postMessage === 'function'
      ) {
        window.webkit.messageHandlers.telemetryHandler.postMessage(event);
      }
    }
  } catch (_) {
    // never throw from error reporter
  }

  return event;
}

let isTelemetryInitialized = false;

/**
 * Attaches global window error and unhandledrejection listeners idempotently.
 */
export function initErrorTelemetry() {
  if (isTelemetryInitialized || typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return;
  }
  isTelemetryInitialized = true;

  window.addEventListener('error', (evt) => {
    reportError(evt?.error || evt?.message || 'WindowError', {
      source: 'window.onerror',
      filename: evt?.filename || '',
      lineno: evt?.lineno || 0,
      colno: evt?.colno || 0
    });
  });

  window.addEventListener('unhandledrejection', (evt) => {
    reportError(evt?.reason || 'UnhandledPromiseRejection', {
      source: 'window.unhandledrejection'
    });
  });
}
